/**
 * File: creditNotes.ts
 * Path: packages/config/src/invoices/creditNotes.ts
 * Description: Credit notes: how a sent invoice is cancelled or reduced.
 *
 * Only a draft, or an issued invoice that was never sent and has no money
 * against it, may be voided. Anything that has left the business is put
 * right with a numbered credit note (CN-000001, no gaps): a reason, who and
 * when, linked to its invoice, booked as Dr the invoice's sales account,
 * Cr Trade debtors tagged with the invoice. Nothing is deleted. Only the
 * server writes (creditNotes/<id>, numbered from creditNoteCounter/main).
 */

import { formatCents } from "../accounting/money";
import type { LedgerInvoice } from "./invoiceLedger";

export const CREDIT_NOTE_COLLECTIONS = {
  creditNotes: "creditNotes",
  counter: "creditNoteCounter",
} as const;

export const CREDIT_NOTE_COUNTER_DOC = "main";

export const formatCreditNoteNumber = (n: number): string => `CN-${String(n).padStart(6, "0")}`;

export const CREDIT_NOTE_REASON_MAX = 300;

export interface CreditNote {
  id: string;
  /** "CN-000001" */
  number: string;
  /** The invoice it credits ("INV-000012"). */
  invoiceNumber: string;
  /** YYYY-MM-DD */
  issuedOn: string;
  cents: number;
  reason: string;
  party: string;
  clientId: string | null;
  /** The journal that booked it. */
  journalId: string;
  createdBy: string;
  createdAt: number | null;
}

const text = (v: unknown, max: number) => (typeof v === "string" ? v.slice(0, max) : "");
const millis = (v: unknown): number | null => {
  if (typeof v === "number") return v;
  const m = (v as { toMillis?: () => number } | null)?.toMillis;
  return typeof m === "function" ? m.call(v) : null;
};

export const toCreditNote = (id: string, data: unknown): CreditNote | null => {
  const d = (data && typeof data === "object" ? data : {}) as Record<string, unknown>;
  const number = text(d.number, 20);
  const cents = d.cents;
  if (!/^CN-\d{6,}$/.test(number) || typeof cents !== "number" || !Number.isSafeInteger(cents) || cents <= 0) return null;
  return {
    id,
    number,
    invoiceNumber: text(d.invoiceNumber, 20),
    issuedOn: text(d.issuedOn, 10),
    cents,
    reason: text(d.reason, CREDIT_NOTE_REASON_MAX),
    party: text(d.party, 200),
    clientId: typeof d.clientId === "string" && d.clientId ? d.clientId : null,
    journalId: text(d.journalId, 128),
    createdBy: text(d.createdBy, 200),
    createdAt: millis(d.createdAt),
  };
};

/** Why an invoice can't be voided; null when it can (never sent, nothing paid or credited). */
export const voidBlocker = (inv: Pick<LedgerInvoice, "status" | "sent" | "paidCents" | "creditedCents">): string | null => {
  if (inv.status === "void") return "It is already void.";
  if (inv.status === "draft") return null;
  // Never in the books, so there is nothing for a credit note to reverse: void it, the number stays with its reason.
  if (inv.status === "unbooked") return null;
  if (inv.sent) return "It has been sent, so it can only be cancelled with a credit note.";
  if (inv.paidCents !== 0 || inv.creditedCents !== 0) return "Money or a credit note is already against it: use a credit note.";
  return null;
};

/** Why a credit note can't be issued as asked; empty when it can. */
export const creditNoteIssues = (
  inv: Pick<LedgerInvoice, "status" | "balanceCents" | "number">,
  cents: number,
  reason: string,
): string[] => {
  const issues: string[] = [];
  if (inv.status === "void" || inv.status === "draft" || inv.status === "unbooked") issues.push("Only an issued invoice in the books can be credited.");
  if (!Number.isSafeInteger(cents) || cents <= 0) issues.push("Enter an amount above nil.");
  else if (cents > inv.balanceCents) issues.push(`${inv.number} only has ${formatCents(Math.max(0, inv.balanceCents))} left to credit.`);
  if (reason.trim().length < 3) issues.push("Say why it is credited.");
  if (reason.length > CREDIT_NOTE_REASON_MAX) issues.push(`Keep the reason under ${CREDIT_NOTE_REASON_MAX} characters.`);
  return issues;
};
